import { rem } from 'polished';
import { useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { useColorMode } from '@vimeo/bokeh';
import { ThemeDispatchContext } from '@vimeo/core/components/ThemeProvider';
import { translate } from '@vimeo/i18n-runtime';
import { Button, Tip } from '@vimeo/iris/components';
import { LightDark } from '@vimeo/iris/icons';

type Theme = 'light' | 'dark';

interface Props {
  onThemeChange?: (theme: Theme) => void;
  className?: string;
}

export const ThemeSwitcher = ({ onThemeChange, className }: Props) => {
  const { setColorMode } = useColorMode();
  const { toggleTheme } = useContext(ThemeDispatchContext);
  const { name } = useContext(ThemeContext);

  const handleThemeChange = (theme: Theme) => {
    toggleTheme(theme);
    onThemeChange?.(theme);
    setColorMode?.(theme);
  };

  return (
    <Tip
      id="lightDarkTip"
      content={name === 'dark' ? translate('Light mode') : translate('Dark mode')}
    >
      <ButtonIcon
        aria-label={name === 'dark' ? translate('Light mode') : translate('Dark mode')}
        onClick={() => handleThemeChange(name === 'dark' ? 'light' : 'dark')}
        format="secondary"
        icon={<StyledLightDark />}
        variant="outline"
        pill
        id="essential-footer-theme-button"
        size="sm"
        className={className}
      />
    </Tip>
  );
};

const ButtonIcon = styled(Button)<{ elevated?: boolean }>`
  background: ${({ theme }) => theme.elements.bg1};
  box-shadow: rgba(0, 0, 0, 0.04) 0px 0.1875rem 0.375rem 0px;
  margin: 0 ${rem(6)};
  ${({ elevated }) => elevated && `z-index: 2`};

  &:hover {
    background-color: ${({ theme }) => theme.elements.bg1};
    border: ${({ theme }) => `1px solid ${theme.elements.color4}`};
    box-shadow: rgba(0, 0, 0, 0.07) 0px 0.1875rem 0.375rem 0px;
    transform: translateY(-1px) scale(1.001);
  }

  :hover,
  :focus {
    svg {
      color: ${({ theme }) => theme.content.color};
    }
  }
`;

const StyledLightDark = styled(LightDark)`
  color: ${({ theme }) => theme.elements.color1};
`;
